import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Theme, ThemeContextValue } from './types';

const STORAGE_KEY = 'examsreactor:theme';

/**
 * Tri-state: 'light' | 'dark' | 'system'.
 * `system` sigue prefers-color-scheme del OS.
 */
type ThemePreference = Theme | 'system';

function getSystemTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  children: ReactNode;
  /** Default preference if nothing is stored. Defaults to 'system'. */
  defaultPreference?: ThemePreference;
  /** Override storage key (useful for embed/test). */
  storageKey?: string;
}

export function ThemeProvider({
  children,
  defaultPreference = 'system',
  storageKey = STORAGE_KEY,
}: ThemeProviderProps) {
  const [preference, setPreference] = useState<ThemePreference>(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return defaultPreference;
  });

  const [systemTheme, setSystemTheme] = useState<Theme>(() => getSystemTheme());

  // Listen to OS theme changes when in `system` mode
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const resolvedTheme: Theme = preference === 'system' ? systemTheme : preference;

  // Apply to <html class="dark" | no class>
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', resolvedTheme === 'dark');
    document.documentElement.setAttribute('data-theme', resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback(
    (next: ThemePreference) => {
      setPreference(next);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, next);
      }
    },
    [storageKey],
  );

  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setTheme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme: preference === 'system' ? systemTheme : preference,
      resolvedTheme,
      setTheme,
      toggleTheme,
      isSystem: preference === 'system',
    }),
    [preference, systemTheme, resolvedTheme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
